import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ApiUserDto {
  /** Present when the list API returns it; required for edit navigation (may be sent as string) */
  userId?: number | string;
  /** Some APIs use `id` instead of `userId` */
  id?: number | string;
  /** Primary site from list API (`SiteId` / `siteId`) */
  siteId?: number | string;
  username: string;
  createDate: string;
  status: string;
  lastUpdatedDate: string | null;
  nameIdentifier: string | null;
  userStatusChangedDate: string | null;
}

/** Payload sent inside `userDetails` for create/update */
export interface ApiUserDetailsPayload {
  userId: number;
  email: string;
  username: string;
  validUntil: string;
  siteIds: number[];
  isActive: boolean;
  /** Required on create; omit on update when not changing password */
  password?: string;
  confirmPassword?: string;
}

export interface ApiUserSaveRequestBody {
  userDetails: ApiUserDetailsPayload;
}

/**
 * Unwrapped `data` from GET /api/APIUser/{userId} (after BaseApiResponse unwrap).
 * Optional fields may be added by the API later (email, validUntil, siteIds, isActive).
 */
export interface ApiUserByIdDto {
  userId: number;
  username: string;
  createDate?: string;
  status?: string;
  lastUpdatedDate?: string | null;
  nameIdentifier?: string | null;
  userStatusChangedDate?: string | null;
  email?: string;
  validUntil?: string;
  /** May appear as `SiteIds` / `siteId` from some API payloads */
  siteIds?: number[] | string[];
  siteId?: number | string;
  isActive?: boolean;
}

/**
 * Body of `data` after BaseApiResponse unwrap (see response-unwrap interceptor).
 */
export interface ApiUsersListResponse {
  success: boolean;
  message: string | null;
  users: ApiUserDto[];
}

@Injectable({
  providedIn: 'root'
})
export class ApiUserService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiBaseUrl;

  /**
   * GET /api/APIUser — list API users for the current client context.
   */
  getApiUsers(): Observable<ApiUsersListResponse> {
    const endpoint = `${this.apiUrl}/APIUser`;
    return this.http.get<ApiUsersListResponse>(endpoint);
  }

  /**
   * GET /api/APIUser/{userId} — returns flat user object in `data` (unwrapped by interceptor).
   */
  getApiUserById(userId: number): Observable<ApiUserByIdDto> {
    const endpoint = `${this.apiUrl}/APIUser/${userId}`;
    return this.http.get<ApiUserByIdDto>(endpoint);
  }

  /** POST /api/APIUser — create API user (`userId`: 0). */
  createApiUser(body: ApiUserSaveRequestBody): Observable<unknown> {
    const endpoint = `${this.apiUrl}/APIUser`;
    return this.http.post<unknown>(endpoint, body);
  }

  /** PUT /api/APIUser — update API user. */
  updateApiUser(body: ApiUserSaveRequestBody): Observable<unknown> {
    const endpoint = `${this.apiUrl}/APIUser`;
    return this.http.put<unknown>(endpoint, body);
  }
}
